import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { type AmbientMetric, ApiError, type Binding, type BindingConfig, type Instance, type Protocol, type Setpoint, api, keys, unwrap, useConnection, useDefinition, useInvalidateFleet, useMeta } from '@/api';
import { PROTOCOL_LABELS } from '@/lib/format';
import { FIRST_UNIT, SPACE } from '@/lib/space';

import { AmbientSensorCard } from './Ambient';
import { Badge, Button, Card, CodeBlock, Dialog, Field, Input, ProtocolBadge, Select, Spinner, Switch } from './ui';

function summary(c: BindingConfig): string {
	switch (c.protocol) {
		case 'mqtt':
			return `${c.topic} · ${c.format ?? 'json'}${c.retain ? ' · retained' : ''}${c.homeAssistant ? ' · Home Assistant' : ''}`;
		case 'modbus_tcp':
			return `unit ${c.unitId} · ${c.registerKind ?? 'input'} registers from ${c.baseAddress ?? 0} · ${(c.wordOrder ?? 'abcd').toUpperCase()}${c.writable ? ' · set-points writable' : ''}${c.deviceInfo ? ' · device info' : ''}`;
		case 'opc_ua':
			return `ns=2;s=${c.nodePrefix}.<field>${c.writable ? ' · set-points writable' : ''}`;
		case 'coap':
			return `/${c.path.replace(/^\/+/, '')}`;
		case 'ambient':
			return `sensor ${c.sensorCode} · ${c.metrics?.length ? c.metrics.join(', ') : 'all mapped metrics'}`;
	}
}

function defaultConfig(p: Exclude<Protocol, 'http'>, i: Instance): BindingConfig {
	switch (p) {
		case 'mqtt':
			return { protocol: 'mqtt', topic: `mock/${SPACE}/${i.definitionId.replace('.', '/')}/${i.slug}`, format: 'json', retain: false };
		case 'modbus_tcp':
			return { protocol: 'modbus_tcp', unitId: FIRST_UNIT, baseAddress: 0, wordOrder: 'abcd', registerKind: 'input' };
		case 'opc_ua':
			return { protocol: 'opc_ua', nodePrefix: `${SPACE}.${i.slug}` };
		case 'coap':
			return { protocol: 'coap', path: `${SPACE}/sensors/${i.slug}` };
		case 'ambient':
			return { protocol: 'ambient', sensorCode: i.slug };
	}
}

/** The opt-in for writes: only where the definition has set-points. */
function WritableSwitch({ setpoints, checked, onChange, error, how }: { setpoints: Setpoint[]; checked: boolean; onChange: (on: boolean) => void; error?: string; how: string }) {
	if (!setpoints.length) return null;
	return (
		<div className="col-span-2 flex flex-col gap-1">
			<Switch checked={checked} onChange={onChange} label="Writable set-points" />
			<p className={error ? 'text-xs text-danger' : 'text-[11px] text-fg-muted'}>
				{error ?? (
					<>
						<span className="font-mono">{setpoints.map((s) => s.key).join(', ')}</span>. {how} A write reaches the running sensor at once (saved only with MOCK_PERSIST_WRITES) and is audited.
						Anyone who can reach the port can write them.
					</>
				)}
			</p>
		</div>
	);
}

function ConfigForm({ config, onChange, errors, ambientMetrics, setpoints }: { config: BindingConfig; onChange: (c: BindingConfig) => void; errors: Record<string, string>; ambientMetrics: AmbientMetric[]; setpoints: Setpoint[] }) {
	const e = (k: string) => errors[`/config/${k}`] ?? errors[`/${k}`];
	switch (config.protocol) {
		case 'mqtt':
			return (
				<div className="flex flex-col gap-3">
					<Field label="Topic" error={e('topic')} hint="Status is published retained on <topic>/status.">
						<Input value={config.topic} onChange={(ev) => onChange({ ...config, topic: ev.target.value })} />
					</Field>
					<div className="grid grid-cols-2 gap-3">
						<Field label="Payload">
							<Select value={config.format ?? 'json'} onChange={(ev) => onChange({ ...config, format: ev.target.value as 'json' | 'senml' })}>
								<option value="json">JSON</option>
								<option value="senml">SenML (RFC 8428)</option>
							</Select>
						</Field>
						<div className="flex items-end pb-1.5">
							<Switch checked={!!config.retain} onChange={(retain) => onChange({ ...config, retain })} label="Retain telemetry" />
						</div>
					</div>
					<div className="flex flex-col gap-1">
						<Switch checked={!!config.homeAssistant} onChange={(homeAssistant) => onChange({ ...config, homeAssistant })} label="Home Assistant discovery" />
						<p className={e('homeAssistant') ? 'text-xs text-danger' : 'text-[11px] text-fg-muted'}>
							{e('homeAssistant') ??
								'One entity per field under homeassistant/, grouped as a device, unavailable while stopped; removed when the sensor is deleted. JSON payload only.'}
						</p>
					</div>
				</div>
			);
		case 'modbus_tcp':
			return (
				<div className="grid grid-cols-2 gap-3">
					<Field label="Unit id" error={e('unitId')}>
						<Input type="number" min={1} max={247} value={config.unitId} onChange={(ev) => onChange({ ...config, unitId: Number(ev.target.value) })} />
					</Field>
					<Field label="Base address" error={e('baseAddress')} hint="0-based PDU address">
						<Input type="number" min={0} max={65535} value={config.baseAddress ?? 0} onChange={(ev) => onChange({ ...config, baseAddress: Number(ev.target.value) })} />
					</Field>
					<Field label="Registers">
						<Select value={config.registerKind ?? 'input'} onChange={(ev) => onChange({ ...config, registerKind: ev.target.value as 'input' | 'holding' })}>
							<option value="input">Input (FC 4)</option>
							<option value="holding">Holding (FC 3)</option>
						</Select>
					</Field>
					<Field label="Float word order">
						<Select value={config.wordOrder ?? 'abcd'} onChange={(ev) => onChange({ ...config, wordOrder: ev.target.value as 'abcd' | 'cdab' })}>
							<option value="abcd">ABCD (big-endian)</option>
							<option value="cdab">CDAB (word swap)</option>
						</Select>
					</Field>
					<WritableSwitch
						setpoints={setpoints}
						checked={!!config.writable}
						onChange={(writable) => onChange({ ...config, writable })}
						error={e('writable')}
						how="One holding register each after the map (int16 × 10^decimals), FC6/FC16."
					/>
					<div className="col-span-2 flex flex-col gap-1">
						<Switch checked={!!config.deviceInfo} onChange={(deviceInfo) => onChange({ ...config, deviceInfo })} label="Device-info block" />
						<p className={e('deviceInfo') ? 'text-xs text-danger' : 'text-[11px] text-fg-muted'}>
							{e('deviceInfo') ??
								'32 registers at base + 100 (the next hundred after a longer map), FC3 or FC4: definition id and version, uptime, status bits, sample interval and number. Read-only.'}
						</p>
					</div>
				</div>
			);
		case 'opc_ua':
			return (
				<div className="grid grid-cols-2 gap-3">
					<Field label="Node prefix" error={e('nodePrefix')} hint="Variables become ns=2;s=<prefix>.<field>" className="col-span-2">
						<Input value={config.nodePrefix} onChange={(ev) => onChange({ ...config, nodePrefix: ev.target.value })} />
					</Field>
					<WritableSwitch
						setpoints={setpoints}
						checked={!!config.writable}
						onChange={(writable) => onChange({ ...config, writable })}
						error={e('writable')}
						how="Variables <prefix>.setpoints.<key> with write access."
					/>
				</div>
			);
		case 'coap':
			return (
				<Field label="Resource path" error={e('path')}>
					<Input value={config.path} onChange={(ev) => onChange({ ...config, path: ev.target.value })} />
				</Field>
			);
		case 'ambient': {
			const all = ambientMetrics.map((m) => m.metric);
			const selected = config.metrics?.length ? config.metrics : all;
			const toggle = (metric: string, on: boolean) => {
				const next = all.filter((m) => (m === metric ? on : selected.includes(m)));
				onChange({ ...config, metrics: next.length === all.length ? [] : next });
			};
			const metricsError = Object.entries(errors).find(([k]) => k.includes('/metrics'))?.[1];
			return (
				<div className="flex flex-col gap-3">
					<Field label="Sensor code" error={e('sensorCode')} hint="The sensor must exist in slims-ambient with these metrics declared.">
						<Input value={config.sensorCode} onChange={(ev) => onChange({ ...config, sensorCode: ev.target.value })} />
					</Field>
					<Field label="Metrics sent" error={metricsError} hint="Send only what the sensor declares; the rest would be rejected as metric_not_declared.">
						<div className="flex flex-col gap-1.5">
							{ambientMetrics.map((m) => (
								<Switch
									key={m.metric}
									checked={selected.includes(m.metric)}
									onChange={(on) => toggle(m.metric, on)}
									label={
										<span className="font-mono text-xs">
											{m.metric} <span className="text-fg-muted">← {m.field}{m.scale !== 1 ? ` × ${m.scale}` : ''}{m.delta ? ' (delta)' : ''}</span>
										</span>
									}
								/>
							))}
						</div>
					</Field>
				</div>
			);
		}
	}
}

function BindingDialog({ instance, binding, initial, ambientMetrics, onClose }: { instance: Instance; binding?: Binding; initial: BindingConfig; ambientMetrics: AmbientMetric[]; onClose: () => void }) {
	const [config, setConfig] = useState<BindingConfig>(initial);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const setpoints = useDefinition(instance.definitionId).data?.setpoints ?? [];
	const qc = useQueryClient();
	const invalidate = useInvalidateFleet();
	const save = useMutation({
		mutationFn: () =>
			binding
				? unwrap(api.PUT('/api/v1/bindings/{id}', { params: { path: { id: binding.id }, header: { 'If-Match': String(binding.revision) } }, body: { enabled: binding.enabled, config } }))
				: unwrap(api.POST('/api/v1/instances/{id}/bindings', { params: { path: { id: instance.id } }, body: { enabled: true, config } })),
		onSuccess: () => {
			toast.success('Binding saved — live now');
			qc.invalidateQueries({ queryKey: keys.instance(instance.id) });
			qc.invalidateQueries({ queryKey: keys.connection(instance.id) });
			invalidate();
			onClose();
		},
		onError: (e) => {
			if (e instanceof ApiError) setErrors(e.fieldErrors());
			toast.error(e.message);
		},
	});
	return (
		<Dialog open onClose={onClose} title={`${binding ? 'Edit' : 'Add'} ${PROTOCOL_LABELS[config.protocol]} binding`}>
			<form
				className="flex flex-col gap-4"
				onSubmit={(e) => {
					e.preventDefault();
					setErrors({});
					save.mutate();
				}}
			>
				<ConfigForm config={config} onChange={setConfig} errors={errors} ambientMetrics={ambientMetrics} setpoints={setpoints} />
				<div className="flex justify-end gap-2">
					<Button onClick={onClose}>Cancel</Button>
					<Button variant="primary" type="submit" disabled={save.isPending}>
						Save
					</Button>
				</div>
			</form>
		</Dialog>
	);
}

export function Bindings({ instance, supported, ambientMetrics }: { instance: Instance; supported: Protocol[]; ambientMetrics: AmbientMetric[] }) {
	const meta = useMeta();
	const conn = useConnection(instance.id);
	const qc = useQueryClient();
	const invalidate = useInvalidateFleet();
	const [editing, setEditing] = useState<{ binding?: Binding; initial: BindingConfig }>();
	const [adding, setAdding] = useState<Exclude<Protocol, 'http'>>('mqtt');
	const refresh = () => {
		qc.invalidateQueries({ queryKey: keys.instance(instance.id) });
		qc.invalidateQueries({ queryKey: keys.connection(instance.id) });
		invalidate();
	};
	const enabled = (p: Protocol) => meta.data?.protocols.find((x) => x.protocol === p)?.enabled ?? false;
	// slims-ambient bindings belong to the ambient space.
	const pushesToAmbient = SPACE === 'ambient' && ambientMetrics.length > 0;
	const addable = [...supported, ...(pushesToAmbient ? (['ambient'] as const) : [])].filter((p): p is Exclude<Protocol, 'http'> => p !== 'http');
	const off = (p: Protocol) => (p === 'ambient' ? 'sink off' : 'listener off');

	const toggle = async (b: Binding, on: boolean) => {
		try {
			await unwrap(api.PUT('/api/v1/bindings/{id}', { params: { path: { id: b.id }, header: { 'If-Match': String(b.revision) } }, body: { enabled: on, config: b.config } }));
			refresh();
		} catch (e) {
			toast.error((e as Error).message);
		}
	};
	const remove = async (b: Binding) => {
		if (!confirm('Remove this binding? Clients on that address stop receiving data.')) return;
		try {
			await unwrap(api.DELETE('/api/v1/bindings/{id}', { params: { path: { id: b.id } } }));
			refresh();
		} catch (e) {
			toast.error((e as Error).message);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<Card
				title="Bindings"
				actions={
					<div className="flex items-center gap-2">
						<Select value={adding} onChange={(e) => setAdding(e.target.value as typeof adding)} className="h-7 w-auto py-0 text-xs">
							{addable.map((p) => (
								<option key={p} value={p}>
									{PROTOCOL_LABELS[p]}
									{enabled(p) ? '' : ` (${off(p)})`}
								</option>
							))}
						</Select>
						<Button size="sm" onClick={() => setEditing({ initial: defaultConfig(adding, instance) })}>
							<Plus className="size-3.5" /> Add
						</Button>
					</div>
				}
			>
				<ul className="divide-y divide-border">
					<li className="flex items-center gap-3 py-2 text-sm">
						<ProtocolBadge protocol="http" />
						<span className="flex-1 font-mono text-xs">
							/data/v1/{SPACE}/sensors/{instance.slug}
						</span>
						<span className="text-xs text-fg-muted">always on</span>
					</li>
					{instance.bindings.map((b) => (
						<li key={b.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
							<ProtocolBadge protocol={b.config.protocol} muted={!b.enabled} />
							<span className="min-w-0 flex-1 truncate font-mono text-xs">{summary(b.config)}</span>
							{!enabled(b.config.protocol) && <Badge tone="warn">{off(b.config.protocol)}</Badge>}
							<Switch checked={b.enabled} onChange={(on) => toggle(b, on)} />
							<Button size="sm" variant="ghost" aria-label="Edit" onClick={() => setEditing({ binding: b, initial: b.config })}>
								<Pencil className="size-3.5" />
							</Button>
							<Button size="sm" variant="ghost" aria-label="Remove" onClick={() => remove(b)}>
								<Trash2 className="size-3.5" />
							</Button>
						</li>
					))}
				</ul>
			</Card>

			<AmbientSensorCard instance={instance} ambientMetrics={ambientMetrics} />

			<Card title="Connect a client">
				{!conn.data ? (
					<Spinner />
				) : (
					<div className="flex flex-col gap-5">
						{conn.data.map((c) => (
							<section key={c.bindingId ?? c.protocol} className="flex flex-col gap-2">
								<h3 className="flex items-center gap-2 text-sm font-medium">
									<ProtocolBadge protocol={c.protocol} muted={!c.enabled} /> {c.title}
									{!c.enabled && <Badge tone="warn">not served</Badge>}
								</h3>
								{c.snippets.map((s) => (
									<CodeBlock key={s.label} label={s.label} code={s.code} />
								))}
								{c.registerMap && (
									<table className="w-full max-w-md text-xs">
										<thead className="text-left text-fg-muted">
											<tr>
												<th className="py-1 font-medium">Register</th>
												<th className="py-1 font-medium">Field</th>
												<th className="py-1 font-medium">Type</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-border font-mono">
											{c.registerMap.entries.map((e) => {
												const base = instance.bindings.find((b) => b.id === c.bindingId)?.config;
												const at = (base?.protocol === 'modbus_tcp' ? (base.baseAddress ?? 0) : 0) + e.offset;
												return (
													<tr key={e.key}>
														<td className="py-1">{at}</td>
														<td className="py-1">{e.key}</td>
														<td className="py-1">{e.encoding}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								)}
							</section>
						))}
					</div>
				)}
			</Card>
			{editing && <BindingDialog instance={instance} binding={editing.binding} initial={editing.initial} ambientMetrics={ambientMetrics} onClose={() => setEditing(undefined)} />}
		</div>
	);
}
